import React, { useState, useRef, useEffect } from "react";
import "./TextArea.less";

interface TextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  characterLimit?: number;
  label: string;
  onInteractionStart?: () => void;
  disabled?: boolean;
}

const TextArea: React.FC<TextAreaProps> = ({
  value,
  onChange,
  placeholder = "Type your message...",
  characterLimit = 500,
  label,
  onInteractionStart,
  disabled = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [isAtLimit, setIsAtLimit] = useState(false);
  const interactionTracked = useRef(false);
  const textareaId = React.useId();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    if (!interactionTracked.current && onInteractionStart) {
      onInteractionStart();
      interactionTracked.current = true;
    }

    if (!characterLimit || newValue.length <= characterLimit) {
      onChange(newValue);
      if (isAtLimit) {
        setIsAtLimit(false);
      }
    } else {
      // Flash when attempting to exceed the limit
      setIsFlashing(true);
      setTimeout(() => {
        setIsFlashing(false);
      }, 250);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);

    if (!interactionTracked.current && onInteractionStart) {
      onInteractionStart();
      interactionTracked.current = true;
    }
  };

  const remainingCharacters = characterLimit ? characterLimit - value.length : 0;
  const isCharacterLimitReached = remainingCharacters === 0;

  // Update isAtLimit when we reach the character limit
  useEffect(() => {
    setIsAtLimit(isCharacterLimitReached);
  }, [isCharacterLimitReached]);

  return (
    <div className="questionnaire-textarea-container">
      <label htmlFor={textareaId} className="visually-hidden">
        {label}
      </label>
      <textarea
        id={textareaId}
        value={value}
        onChange={handleChange}
        placeholder={isFocused ? "" : placeholder}
        className={`questionnaire-textarea character-limit ${isFlashing ? "flash" : ""} ${isAtLimit ? "at-limit" : ""}`}
        onFocus={handleFocus}
        onBlur={() => setIsFocused(false)}
        aria-describedby={characterLimit ? `${textareaId}-limit` : undefined}
        disabled={disabled}
      />
      {characterLimit && (
        <div
          id={`${textareaId}-limit`}
          className={`character-limit ${isFlashing ? "flash" : ""} ${isAtLimit ? "at-limit" : ""}`}
          aria-live="polite"
        >
          {remainingCharacters}{" "}
          {remainingCharacters === characterLimit
            ? "character limit"
            : remainingCharacters === 1
              ? "character remaining"
              : "characters remaining"}
        </div>
      )}
    </div>
  );
};

export default TextArea;
